'use client';

import { DotsThree, House } from '@phosphor-icons/react/ssr';
import {
  Children,
  cloneElement,
  type ComponentProps,
  type ElementType,
  Fragment,
  isValidElement,
  type ReactNode,
  useEffect,
  useRef,
  useState,
} from 'react';

import { tv } from '../../lib/tv';
import {
  Breadcrumb as BreadcrumbNav,
  BreadcrumbItem as BreadcrumbLi,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator as BreadcrumbSeparatorLi,
} from '../ui/breadcrumb';
import { DropdownMenuItem } from '../ui/dropdown-menu';
import { IconButton } from './icon-button';
import { Menu, MenuContent, MenuTrigger } from './menu';
import { Tooltip } from './tooltip';

/**
 * Figma Breadcrumb (79:105), Breadcrumb Item (79:36), Breadcrumb Separator
 * (79:37) and Breadcrumb Overflow (79:40), page ↳ Breadcrumb.
 *
 * Item: 2px × 4px padding, radius/xs, gap space/1_5. The label stack is the
 * label (Body/Small, truncated at 200px) with a 1px underline 1px below it in
 * the label colour, shown on hover → 24 tall. Link: text/secondary, Hover
 * text/primary + underline. Current: Body/Small Strong text/primary, not a
 * link. Home: 16px house icon/secondary (icon/primary on hover), 24×20. Focus:
 * 2px border/focus outside stroke. Separator: 16px box, 12px caret-right
 * icon/tertiary. Overflow: Small Ghost Icon Button at 24×24 with dots-three.
 * Breadcrumb: items and separators space/1 apart.
 */
export const breadcrumbStyles = tv({
  slots: {
    list: 'gap-1',
    item: [
      'group/crumb inline-flex min-w-0 items-center gap-1.5 rounded-xs px-1 py-0.5 outline-none',
      'text-body-sm text-text-secondary transition-colors duration-[120ms] ease-out',
      'focus-visible:ring-2 focus-visible:ring-border-focus',
    ],
    stack: 'flex min-w-0 flex-col gap-px',
    label: 'block max-w-[200px] truncate',
    underline: [
      'h-px w-full bg-current opacity-0 transition-opacity duration-[120ms] ease-out',
    ],
    icon: 'size-3.5 shrink-0',
    homeIcon: [
      'size-4 shrink-0 text-icon-secondary transition-colors duration-[120ms] ease-out',
      'group-hover/crumb:text-icon-primary',
    ],
    separator:
      'flex size-4 shrink-0 items-center justify-center text-icon-tertiary [&_svg]:size-3',
    overflow: 'size-6',
  },
  variants: {
    kind: {
      link: {
        item: 'cursor-pointer hover:text-text-primary',
        underline: 'group-hover/crumb:opacity-100',
      },
      current: { item: 'text-body-sm-strong text-text-primary' },
      home: { item: 'cursor-pointer' },
    },
  },
  defaultVariants: { kind: 'link' },
});

/** Measures whether the label is cut off, to show the full text in a Tooltip. */
function useTruncated() {
  const ref = useRef<HTMLSpanElement>(null);
  const [truncated, setTruncated] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    // Fires once on observe, then on every resize.
    const observer = new ResizeObserver(() =>
      setTruncated(el.scrollWidth > el.clientWidth),
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  return [ref, truncated] as const;
}

export type BreadcrumbItemProps = Omit<ComponentProps<'a'>, 'children'> & {
  /** Figma "Label". For `home`, the accessible name (default "Home"). */
  children?: ReactNode;
  /** Figma State=Current: the page you are on - SemiBold, not a link, `aria-current="page"`. */
  current?: boolean;
  /** Figma Type=Home: icon-only house link. */
  home?: boolean;
  /** Figma "Leading icon" - a 14px icon before the label. */
  leadingIcon?: ElementType<{ className?: string }>;
  /** Render the single child (a router Link) with breadcrumb styling. */
  asChild?: boolean;
};

/**
 * One level of the path (Figma Breadcrumb Item), rendered as its `<li>`. Long
 * labels truncate at 200px and show the full text in a Tooltip (needs the
 * app's `TooltipProvider`).
 */
export function BreadcrumbItem({
  children,
  current = false,
  home = false,
  leadingIcon: LeadingIcon,
  asChild = false,
  className,
  ...props
}: BreadcrumbItemProps) {
  const kind = current ? 'current' : home ? 'home' : 'link';
  const styles = breadcrumbStyles({ kind });
  const [labelRef, truncated] = useTruncated();
  const [open, setOpen] = useState(false);

  const stack = (label: ReactNode) => (
    <span className={styles.stack()}>
      <span ref={labelRef} data-slot="breadcrumb-label" className={styles.label()}>
        {label}
      </span>
      <span aria-hidden data-slot="breadcrumb-underline" className={styles.underline()} />
    </span>
  );

  if (home && !current) {
    return (
      <BreadcrumbLi>
        <BreadcrumbLink
          asChild={asChild}
          data-kind="home"
          aria-label={asChild ? undefined : ((children as string | undefined) ?? 'Home')}
          className={styles.item({ className })}
          {...props}
        >
          {asChild && isValidElement(children) ? (
            children
          ) : (
            <House aria-hidden className={styles.homeIcon()} />
          )}
        </BreadcrumbLink>
      </BreadcrumbLi>
    );
  }

  const child =
    asChild && isValidElement<{ children?: ReactNode }>(children) ? children : null;
  const body = (
    <>
      {LeadingIcon && <LeadingIcon aria-hidden className={styles.icon()} />}
      {child ? null : stack(children)}
    </>
  );
  const labelText = child ? child.props.children : children;

  const element = current ? (
    <BreadcrumbPage
      data-kind="current"
      // Reachable by keyboard only when cut off, so the Tooltip can show.
      tabIndex={truncated ? 0 : undefined}
      className={styles.item({ className })}
    >
      {body}
    </BreadcrumbPage>
  ) : (
    <BreadcrumbLink
      asChild={Boolean(child)}
      data-kind="link"
      className={styles.item({ className })}
      {...props}
    >
      {child
        ? cloneElement(
            child,
            undefined,
            <>
              {LeadingIcon && <LeadingIcon aria-hidden className={styles.icon()} />}
              {stack(child.props.children)}
            </>,
          )
        : body}
    </BreadcrumbLink>
  );

  return (
    <BreadcrumbLi>
      <Tooltip content={labelText} open={open && truncated} onOpenChange={setOpen}>
        {element}
      </Tooltip>
    </BreadcrumbLi>
  );
}

/** Figma Breadcrumb Separator: a decorative caret between levels. */
export function BreadcrumbSeparator({ className }: { className?: string }) {
  return (
    <BreadcrumbSeparatorLi className={breadcrumbStyles().separator({ className })} />
  );
}

export type BreadcrumbEntry = {
  /** Visible label (the accessible name for a `home` entry). */
  label: string;
  /** Where the level links to. The last entry is the current page and needs none. */
  href?: string;
  /** Icon-only house link (Figma Type=Home). */
  home?: boolean;
  /** 14px icon before the label. */
  icon?: ElementType<{ className?: string }>;
};

export type BreadcrumbOverflowProps = {
  /** The hidden levels, listed in a Menu. */
  items: BreadcrumbEntry[];
  /** Link component for the menu items (e.g. Next.js `Link`); default `<a>`. */
  linkAs?: ElementType;
  /** Names the trigger. */
  label?: string;
};

/** Figma Breadcrumb Overflow: "…" that opens a Menu of the hidden levels. */
export function BreadcrumbOverflow({
  items,
  linkAs: LinkAs = 'a',
  label = 'Show hidden levels',
}: BreadcrumbOverflowProps) {
  return (
    <BreadcrumbLi>
      <Menu>
        <Tooltip content={label}>
          <MenuTrigger asChild>
            <IconButton
              data-slot="breadcrumb-overflow"
              icon={DotsThree}
              size="sm"
              variant="ghost"
              aria-label={label}
              className={breadcrumbStyles().overflow()}
            />
          </MenuTrigger>
        </Tooltip>
        <MenuContent>
          {items.map((item) => (
            <DropdownMenuItem key={`${item.href}-${item.label}`} asChild>
              <LinkAs href={item.href}>
                {item.icon && <item.icon aria-hidden />}
                <span className="min-w-0 flex-1 truncate">{item.label}</span>
              </LinkAs>
            </DropdownMenuItem>
          ))}
        </MenuContent>
      </Menu>
    </BreadcrumbLi>
  );
}

export type BreadcrumbProps = Omit<ComponentProps<'nav'>, 'children'> & {
  /**
   * The path, root first; the last entry is the current page. Longer than
   * `maxItems` collapses to the first level (plus the one after Home) + "…" +
   * the last two (Figma Length=Collapsed).
   */
  items?: BreadcrumbEntry[];
  /** Figma Length=Short holds up to 4 levels. */
  maxItems?: number;
  /** Link component for every level (e.g. Next.js `Link`); default `<a>`. */
  linkAs?: ElementType;
  /**
   * Compose by hand instead of `items`: `BreadcrumbItem`s (and optionally a
   * `BreadcrumbOverflow`). Separators are placed between them. Pass an array or
   * separate elements - a fragment counts as one child.
   */
  children?: ReactNode;
};

/**
 * Where the page sits in the hierarchy, with one-click travel up (Figma
 * Breadcrumb). Place above the page title with an 8px gap. `nav` named
 * "Breadcrumb" › `ol`; the current page carries `aria-current="page"`. Not for
 * linear steps (use a stepper) or as the only navigation.
 */
export function Breadcrumb({
  items,
  maxItems = 4,
  linkAs,
  children,
  className,
  ...props
}: BreadcrumbProps) {
  const styles = breadcrumbStyles();
  let nodes: ReactNode[];
  if (items) {
    const entry = (item: BreadcrumbEntry, index: number) => {
      const current = index === items.length - 1;
      const LinkAs = linkAs;
      if (!LinkAs || current)
        return (
          <BreadcrumbItem
            key={`${index}-${item.label}`}
            href={current ? undefined : item.href}
            current={current}
            home={item.home}
            leadingIcon={item.icon}
          >
            {item.label}
          </BreadcrumbItem>
        );
      return (
        <BreadcrumbItem
          key={`${index}-${item.label}`}
          asChild
          home={item.home}
          aria-label={item.home ? item.label : undefined}
          leadingIcon={item.icon}
        >
          <LinkAs href={item.href}>
            {item.home ? <House aria-hidden className={styles.homeIcon()} /> : item.label}
          </LinkAs>
        </BreadcrumbItem>
      );
    };
    if (items.length > maxItems) {
      const before = items[0]?.home ? 2 : 1;
      const after = 2;
      nodes = [
        ...items.slice(0, before).map((item, i) => entry(item, i)),
        <BreadcrumbOverflow
          key="overflow"
          items={items.slice(before, items.length - after)}
          linkAs={linkAs}
        />,
        ...items
          .slice(items.length - after)
          .map((item, i) => entry(item, items.length - after + i)),
      ];
    } else nodes = items.map(entry);
  } else nodes = Children.toArray(children);

  return (
    <BreadcrumbNav className={className} {...props}>
      <BreadcrumbList className={styles.list()}>
        {nodes.map((node, i) => (
          <Fragment key={isValidElement(node) && node.key != null ? node.key : i}>
            {i > 0 && <BreadcrumbSeparator />}
            {node}
          </Fragment>
        ))}
      </BreadcrumbList>
    </BreadcrumbNav>
  );
}
